
import { useState, useRef } from 'react';
import { api } from '../../lib/api';

interface MediaUploaderProps {
  type?: 'image' | 'video';
  currentUrl?: string;
  label?: string;
  onUploadComplete?: (url: string) => void;
  onUploadSuccess?: (url: string) => void;
  compact?: boolean;
  className?: string;
}

export default function MediaUploader({
  type = 'image',
  currentUrl,
  label = 'Upload Image',
  onUploadComplete,
  onUploadSuccess,
  compact,
  className
}: MediaUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const timestamp = Date.now();
            const originalName = file.name.replace(/\.[^/.]+$/, '');
            const compressedFile = new File([blob], `${originalName}_${timestamp}.webp`, {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }
        }, 'image/webp', 0.8);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const compressVideo = async (file: File): Promise<File> => {
    // Gerçek zamanlı tarayıcı tabanlı video dönüştürme güvenilir değildir.
    // Orijinal dosyayı, sadece zaman damgası ekleyerek geri döndürüyoruz.
    const timestamp = Date.now();
    const match = file.name.match(/^(.*)(\.[^.]*)$/);
    const base = match ? match[1] : file.name;
    const ext = match ? match[2] : '';
    return new File([file], `${base}_${timestamp}${ext}`, { type: file.type, lastModified: Date.now() });
  };

  const uploadFile = async (file: File) => {
    try {
      setUploading(true);
      setProgress(0);

      // File size validation
      const maxSize = type === 'video' ? 75 * 1024 * 1024 : 50 * 1024 * 1024; // 75MB video, 50MB image
      if (file.size > maxSize) {
        alert(`File size is too large! Maximum allowed is ${type === 'video' ? '75MB' : '50MB'}. Current size: ${formatFileSize(file.size)}`);
        setUploading(false);
        return;
      }

      let processedFile: File;
      
      if (type === 'image') {
        // Image compression and WebP conversion
        setProgress(20);
        processedFile = await compressImage(file);
      } else {
        // Video processing
        setProgress(20);
        processedFile = await compressVideo(file);
        
        if (processedFile.size > maxSize) {
          alert(`Video file is still too large! Please choose a smaller video. Processed size: ${formatFileSize(processedFile.size)}`);
          setUploading(false);
          return;
        }
      }

      setProgress(40);
      setProgress(60);
      
      const data = await api.upload('/media/upload', processedFile);

      if (!data.success) {
        console.error('Upload error:', data.error);
        alert(`Upload error: ${data.error}`);
        setUploading(false);
        return;
      }

      setProgress(100);
      if (onUploadComplete) onUploadComplete(data.url);
      else if (onUploadSuccess) onUploadSuccess(data.url);
      
      // Success message
      setTimeout(() => {
        alert(`${type === 'video' ? 'Video' : 'Image'} uploaded successfully!`);
        setUploading(false);
        setProgress(0);
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      alert('An error occurred during upload!');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.target.files[0]);
    }
  };

  const acceptedTypes = type === 'video' 
    ? 'video/mp4,video/webm,video/quicktime,video/avi'
    : 'image/jpeg,image/png,image/webp,image/gif';

  if (compact) {
    return (
      <div className={`relative ${className || ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-lg border border-blue-200 flex items-center justify-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              <span>Uploading {progress}%...</span>
            </>
          ) : (
            <>
              <i className="ri-upload-cloud-line text-sm"></i>
              <span>{label || (currentUrl ? 'Change Image' : 'Upload Image')}</span>
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Current file preview */}
      {currentUrl && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Current {type === 'video' ? 'Video' : 'Image'}:</p>
          {type === 'video' ? (
            <video src={currentUrl} className="w-full max-h-48 rounded border" controls />
          ) : (
            <img src={currentUrl} alt="Current" className="w-full max-h-48 object-cover rounded border" />
          )}
          <p className="text-xs text-gray-500 mt-1 break-all">{currentUrl}</p>
        </div>
      )}

      {/* Upload area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : uploading
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto">
              <i className="ri-upload-cloud-2-line text-4xl text-blue-600"></i>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                {progress < 40 ? 'Processing file...' : progress < 80 ? 'Uploading...' : 'Finalizing...'}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{progress}%</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto">
              <i className={`${type === 'video' ? 'ri-video-line' : 'ri-image-line'} text-4xl text-gray-400`}></i>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                Drag and drop or select a {type === 'video' ? 'video' : 'image'} file
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {type === 'video' 
                  ? 'MP4, WebM, QuickTime, AVI - Max 75MB'
                  : 'JPEG, PNG, WebP, GIF - Max 50MB'
                }
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                Choose File
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Format information */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
        <p className="font-medium mb-1">Automated Processing:</p>
        <ul className="space-y-1">
          {type === 'video' ? (
            <>
              <li>• Video size validation (max 75MB)</li>
              <li>• WebM format conversion (if supported)</li>
              <li>• Video compression (1280x720px max)</li>
              <li>• Timestamp added to filename</li>
            </>
          ) : (
            <>
              <li>• Image compression (80% quality)</li>
              <li>• WebP format conversion</li>
              <li>• Maximum dimensions: 1920x1080px</li>
              <li>• Timestamp added to filename</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}
